// Container Trader: Meta (Facebook) Pixel and conversion events.
// The Pixel is loaded by initMetaPixel() from src/index.js on the client. Page views on in-app navigation are
// sent from analytics/handlers.js (MetaPixelHandler).

export const GTM_ID = process.env.REACT_APP_GTM_ID || 'GTM-K4G2DBQV';

export const META_PIXEL_ID = process.env.REACT_APP_META_PIXEL_ID || '1550119540488309';

const hasFbq = () => typeof window !== 'undefined' && typeof window.fbq === 'function';

const moneyToNumber = money =>
  money && typeof money.amount === 'number' ? Math.round(money.amount) / 100 : null;

// Pushes an event to the Google Tag Manager dataLayer (for Google Ads conversions set up in GTM).
const pushDataLayer = payload => {
  try {
    if (typeof window !== 'undefined') {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(payload);
    }
  } catch (e) {
    // ignore
  }
};

// Sends a standard Meta event. Never throws: tracking must not break the site.
export const trackMetaEvent = (eventName, params) => {
  try {
    if (hasFbq()) {
      if (params) {
        window.fbq('track', eventName, params);
      } else {
        window.fbq('track', eventName);
      }
    }
  } catch (e) {
    // ignore
  }
};

// Account created (email or Google/Facebook signup)
export const trackSignup = userType => {
  trackMetaEvent('CompleteRegistration', userType ? { content_name: userType } : undefined);
  pushDataLayer({ event: 'ct_sign_up', user_type: userType || null });
};

// Seller hit their plan's active-listing limit
export const trackListingLimitReached = (planId, limit) => {
  pushDataLayer({ event: 'ct_listing_limit_reached', seller_plan: planId || null, limit });
};

// Seller asked to upgrade to a higher plan
export const trackSellerUpgradeRequest = (fromPlan, toPlan) => {
  trackMetaEvent('SubmitApplication', { content_name: toPlan || undefined });
  pushDataLayer({ event: 'ct_seller_upgrade_request', from_plan: fromPlan, to_plan: toPlan });
};

// Buyer sent a message to a seller about a listing
export const trackInquiry = listing => {
  trackMetaEvent('Lead', {
    content_ids: listing?.id?.uuid ? [listing.id.uuid] : undefined,
    content_type: 'product',
  });
  pushDataLayer({ event: 'ct_generate_lead', listing_id: listing?.id?.uuid || null });
};

// Buyer completed a Buy It Now checkout
export const trackPurchase = (transaction, listing) => {
  const payinTotal = transaction?.attributes?.payinTotal;
  const value = moneyToNumber(payinTotal);
  trackMetaEvent('Purchase', {
    value: value || 0,
    currency: payinTotal?.currency || 'USD',
    content_ids: listing?.id?.uuid ? [listing.id.uuid] : undefined,
    content_type: 'product',
  });
  pushDataLayer({
    event: 'ct_purchase',
    value: value || 0,
    currency: payinTotal?.currency || 'USD',
    listing_id: listing?.id?.uuid || null,
  });
};

// Loads Google Tag Manager (same as Google's snippet).
export const initGtm = () => {
  if (typeof window === 'undefined' || !GTM_ID || window.__ctGtmLoaded) return;
  window.__ctGtmLoaded = true;
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' });
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtm.js?id=${GTM_ID}`;
  const first = document.getElementsByTagName('script')[0];
  if (first && first.parentNode) {
    first.parentNode.insertBefore(script, first);
  } else {
    document.head.appendChild(script);
  }
};

// Installs the fbq stub and queues init + first PageView (same as Meta's base code).
export const initMetaPixel = () => {
  if (typeof window === 'undefined' || window.fbq || !META_PIXEL_ID) return;
  const n = function() {
    n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
  };
  window.fbq = n;
  if (!window._fbq) window._fbq = n;
  n.push = n;
  n.loaded = true;
  n.version = '2.0';
  n.queue = [];
  // Load fbevents.js after the stub exists (the script expects a global fbq)
  const script = document.createElement('script');
  script.async = true;
  script.src = 'https://connect.facebook.net/en_US/fbevents.js';
  const first = document.getElementsByTagName('script')[0];
  if (first && first.parentNode) {
    first.parentNode.insertBefore(script, first);
  } else {
    document.head.appendChild(script);
  }
  window.fbq('init', META_PIXEL_ID);
  window.fbq('track', 'PageView');
};

// Buyer got an instant delivery quote on a listing (custom Meta event "DeliveryQuote").
// In Meta Events Manager, create a Custom Conversion from this event to optimize ads for quotes.
export const trackDeliveryQuote = ({
  listingId,
  deliveryType,
  requiresApproval,
  deliveredTotalCents,
}) => {
  try {
    if (hasFbq()) {
      const value =
        typeof deliveredTotalCents === 'number' ? Math.round(deliveredTotalCents) / 100 : undefined;
      window.fbq('trackCustom', 'DeliveryQuote', {
        content_ids: listingId ? [listingId] : undefined,
        content_type: 'product',
        delivery_type: deliveryType || undefined,
        requires_approval: !!requiresApproval,
        ...(value ? { quoted_total: value, currency: 'USD' } : {}),
      });
    }
  } catch (e) {
    // ignore
  }
};

// Buyer changed a choice in the Delivery & Fit Checker (GTM event "ct_delivery_checker").
export const trackDeliveryChecker = ({ field, value, variant }) => {
  pushDataLayer({
    event: 'ct_delivery_checker',
    checker_field: field || null,
    checker_value: value || null,
    checker_variant: variant || null,
  });
};
